import Geolocation from "@react-native-community/geolocation";
import haversine from "haversine-distance";

const METERS_TO_MILES_CONVERSION = 0.000621371;

/**
 * Retrieves the current geographical location of the device.
 *
 * @returns {Promise<{latitude: number, longitude: number}>} A promise that resolves to an object containing the latitude and longitude of the current location.
 * @throws {Error} If the location cannot be retrieved, the promise is rejected with an error.
 */
export const getCurrentLocation = async () => {
  return new Promise((resolve, reject) => {
    Geolocation.getCurrentPosition(
      (position) => {
        resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude });
      },
      (error) => {
        reject(error);
      }
    );
  });
};

/**
 * Calculates the distance from the current location to a specified location.
 *
 * @param {Object} location - The target location to calculate the distance to.
 * @param {number} location.lat - The latitude of the target location.
 * @param {number} location.lng - The longitude of the target location.
 * @returns {Promise<string|null>} The distance in miles, rounded to one decimal place, or null if an error occurs.
 */
export const getDistanceFromLocation = async (userLocation, { lat, lng }) => {
  try {
    const distanceMeters = haversine(userLocation, { latitude: lat, longitude: lng });
    return (distanceMeters * METERS_TO_MILES_CONVERSION).toFixed(1);
  } catch (error) {
    console.log("Error calculating distance:", error);
    return null;
  }
};

/**
 * Calculates the distances of a list of locations from the user's current location.
 *
 * @async
 * @function
 * @param {Array<Object>} listOfLocations - An array of location objects. Each object should have either:
 *   - `geometry.y` and `geometry.x` properties, or
 *   - `latitude` and `longitude` properties.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of objects, where each object contains:
 *   - `id` (string|number): The ID of the location.
 *   - `distance` (number): The calculated distance from the user's location.
 * @throws Will log an error message if the user's location cannot be retrieved or if distance calculation fails.
 */
export const getDistancesOfLocationsFromUser = async (listOfLocations) => {
  try {
    const userLocation = await getCurrentLocation();
    const distances = await Promise.all(
      listOfLocations.map(async (loc) => {
        const dist = await getDistanceFromLocation(userLocation, {
          lat: loc.latlng[0],
          lng: loc.latlng[1],
        });
        return { ...loc, distance: dist };
      })
    );

    return distances;
  } catch (err) {
    console.log("Error getting distances of locations from user:", err);
    return null;
  }
};
